import React, {useState, useEffect, useRef} from 'react';
import post from '../utils/post.js';
import AppHeader from '../components/AppHeader.js';
import ConfirmPopup from '../components/ConfirmPopup.js';
import InstructorProfile from '../components/InstructorProfile.js';
import clipboard from '../utils/clipboard.js';
import '../styles/lesson.scss';

const coachNoteCollapsedHeight = 190;

export default function Lesson({usrInfo, timerCnt}) {
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]);
	const [instructors, setInstructors] = useState([]);
	const [selectedInstructor, setSelectedInstructor] = useState(null);
	const [selInstructorId, setSelInstructorId] = useState(0);
	const [selInstructorNote, setSelInstructorNote] = useState('');
	const [usrMax, setUsrMax] = useState(8);
	const [currentList, setCurrentList] = useState(1);
	const [showConfirmPopup, setShowConfirmPopup] = useState(false);
	const [showConfirmConfirmPopup, setShowConfirmConfirmPopup] = useState(false);
	const [currentDeleteId, setCurrentDeleteId] = useState(0);
	const [idForDelete, setIdForDelete] = useState(0);
	const [nameForDelete, setNameForDelete] = useState('');
	const [showQR, setShowQR] = useState(false);
	const [qrData, setQrData] = useState('');
	const [qrAccount, setQrAccount] = useState('');
	const [qrPrice, setQrPrice] = useState(0);
	const [lessonWarning, setLessonWarning] = useState({id: 0, remainingPlaces: 0});
	const [offset, setOffset] = useState(0);
	const [loadedCnt, setLoadedCnt] = useState(0);
	const [isCoachNoteExpanded, setIsCoachNoteExpanded] = useState(false);
	const [canExpandCoachNote, setCanExpandCoachNote] = useState(false);
	const [coachNoteMaxHeight, setCoachNoteMaxHeight] = useState(coachNoteCollapsedHeight);
	const coachNoteContentRef = useRef(null);
	const listRows = parseInt(usrInfo.LIST_ROWS, 10) || 0;

	useEffect(() => {
		getData(selInstructorId, currentList, 0);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setIsCoachNoteExpanded(false);
	}, [selInstructorNote]);

	useEffect(() => {
		const measureCoachNote = () => {
			const nextScrollHeight = coachNoteContentRef.current?.scrollHeight || 0;
			const nextCanExpand = nextScrollHeight > coachNoteCollapsedHeight;
			setCanExpandCoachNote(nextCanExpand);
			setCoachNoteMaxHeight(nextCanExpand ? (isCoachNoteExpanded ? nextScrollHeight : coachNoteCollapsedHeight) : nextScrollHeight);
		};

		measureCoachNote();

		window.addEventListener('resize', measureCoachNote);

		return () => {
			window.removeEventListener('resize', measureCoachNote);
		};
	}, [isCoachNoteExpanded, selInstructorNote]);

	const createInstructorProfileData = (instructor) => {
		if (!instructor) {
			return null;
		}

		const phone = instructor.PHONES || '';

		return {
			id: instructor.CRBY,
			usrName: instructor.USRNAME || '',
			firstName: instructor.FIRSTNAME || '',
			lastName: instructor.LASTNAME || '',
			nickname: instructor.NICKNAME || '',
			phone,
			phoneLink: phone.replace(/\s+/g, ''),
			email: instructor.EMAIL || '',
			profilePhotoUrl: instructor.PROFILE_PHOTO_URL || ''
		};
	};

	const getData = (selInstructorId, targetList = currentList, targetOffset = 0) => {
		setLoading(true);

		post('getListLesson', {INSTRUCTOR_ID: selInstructorId, CURRENT_LIST: targetList, offset: targetOffset})
			.then((res) => {
				const nextInstructorId = parseInt(res.data.INSTRUCTOR_ID, 10) || 0;
				let nextSelectedInstructor = null;

				for (const instructor of res.data.instructors) {
					if (parseInt(instructor.CRBY, 10) === nextInstructorId) {
						nextSelectedInstructor = createInstructorProfileData(instructor);
						break;
					}
				}

				if (targetOffset === 0) {
					setData(res.data.data);
				} else {
					setData((prevRows) => [...prevRows, ...res.data.data]);
				}

				setInstructors(res.data.instructors);
				setSelectedInstructor(nextSelectedInstructor);
				setSelInstructorId(nextInstructorId);
				setSelInstructorNote(res.data.INSTRUCTOR_NOTE);
				setUsrMax(res.data.USERS_MAX);
				setQrAccount(res.data.INSTRUCTOR_ACCOUNT);
				setCurrentList(targetList);
				setOffset(targetOffset);
				setLoadedCnt(res.data.data.length);
				setLessonWarning({id: 0, remainingPlaces: 0});

				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	const selInstructor = (id) => {
		setLessonWarning({id: 0, remainingPlaces: 0});
		setSelInstructorId(id);
		getData(id, currentList, 0);
	}

	const selLesson = (id, addPerson, anonym) => {
		post('selLesson', {ID: id, ADDPERSON: addPerson, ANONYM: anonym})
			.then((res) => {
				if (parseInt(res.result, 10) === 2) {
					setLessonWarning({id: 0, remainingPlaces: 0});
					getData(selInstructorId, currentList, 0);
					return;
				}

				if (parseInt(res.result, 10) === 0) {
					setLessonWarning({
						id: id,
						remainingPlaces: Math.max(parseInt(res.remainingPlaces, 10) || 0, 0)
					});
					return;
				}

				setLessonWarning({id: 0, remainingPlaces: 0});
				getData(selInstructorId, currentList, 0);
			})
			.catch((error) => {
				console.error(error);
			});
	}

	const unselLesson = (id) => {
		post('unselLesson', {ID: id})
			.then((res) => {
				setLessonWarning({id: 0, remainingPlaces: 0});
				getData(selInstructorId, currentList, 0);
			})
			.catch((error) => {
				console.error(error);
			});
	}

	const prevLessons = () => {
		setLessonWarning({id: 0, remainingPlaces: 0});
		if (currentList !== 0) {
			getData(selInstructorId, 0, 0);
		}
	}

	const currLessons = () => {
		setLessonWarning({id: 0, remainingPlaces: 0});
		if (currentList !== 1) {
			getData(selInstructorId, 1, 0);
		}
	}

	const loadNext = () => {
		if (!listRows || loading) {
			return;
		}

		getData(selInstructorId, currentList, offset + listRows);
	}

	const handleConfirmPopup = (lessonId, id, name) => {
		setCurrentDeleteId(lessonId);
		setIdForDelete(id);
		setNameForDelete(name);
		setShowConfirmPopup(true);
	};

	const closeConfirmPopup = () => {
		setShowConfirmPopup(false);
	};

	const deleteConfirmPopup = () => {
		post('deleteLessonUsr', {LESSONID: currentDeleteId, ID: idForDelete})
			.then((res) => {
				getData(selInstructorId, currentList, 0);
				closeConfirmPopup();
			})
			.catch((error) => {
				console.error(error);
				closeConfirmPopup();
			});
	};

	const handleConfirmConfirmPopup = (lessonId, id, name) => {
		setCurrentDeleteId(lessonId);
		setIdForDelete(id);
		setNameForDelete(name);
		setShowConfirmConfirmPopup(true);
	}

	const closeConfirmConfirmPopup = () => {
		setShowConfirmConfirmPopup(false);
	}

	const confirmLesson = () => {
		post('confirmLesson', {LESSONID: currentDeleteId, USRID: idForDelete})
			.then((res) => {
				getData(selInstructorId, currentList, 0);
				setShowConfirmConfirmPopup(false);
			})
			.catch((error) => {
				console.error(error);
				setShowConfirmConfirmPopup(false);
			});
	};

	const showQRPop = (qr, price) => {
		setQrData(qr);
		setQrPrice(price);
		setShowQR(true);
	}

	const closeQR = () => {
		setShowQR(false);
	}

	const saveLessonLocked = (id) => {
		setLoading(true);

		post('saveLessonLocked', {ID: id, CURRENT_LIST: currentList})
			.then(() => {
				getData(selInstructorId, currentList, 0);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	}

	const saveLessonOpened = (id) => {
		setLoading(true);

		post('saveLessonOpened', {ID: id, CURRENT_LIST: currentList})
			.then(() => {
				getData(selInstructorId, currentList, 0);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	}

	const isOwnInstructor = (parseInt(selInstructorId, 10) || 0) === (parseInt(usrInfo.usrId, 10) || 0);
	const viewTitle = currentList === 1 ? 'Nadcházející lekce' : 'Minulé lekce';

	const list = data.map((item, index) => {
		let usersList = [];
		const showLessonWarning = lessonWarning.id === item.ID && item.IM_IN === 0;
		const canToggleLessonLock = currentList === 1 && ((parseInt(item.CRBY, 10) || 0) === (parseInt(usrInfo.usrId, 10) || 0));
		const lessonLockIcon = parseInt(item.LOCKED, 10) === 1 ? '/images/lesson_lock.svg' : '/images/lesson_unlock.svg';
		const lessonLockAlt = parseInt(item.LOCKED, 10) === 1 ? 'Zamčená lekce' : 'Odemčená lekce';
		const isLessonLocked = parseInt(item.LOCKED, 10) === 1;
		const isLessonClosed = parseInt(item.OPENED, 10) === 0;
		const lessonOpenedAlt = isLessonClosed ? 'Přihlašování na lekci je uzavřeno' : 'Přihlašování na lekci je otevřeno';

		if (item.USERS) {
			usersList = item.USERS.map((user, uIndex) => (
				<div
					className={`lesson-page__user-row${usrInfo.usrId === user.USRID ? ' is-self' : ''}${user.USRID === 0 ? ' is-anonym' : ''}${user.STATUS === 1 ? ' is-confirmed' : ''}`}
					key={`user_${uIndex}`}
				>
					<div className="lesson-page__user-main">
						<span className="lesson-page__user-order">{user.ORDER}</span>
						<span className="lesson-page__user-name-wrap">
							<span className="lesson-page__user-name">{user.USRNAME}</span>
							{parseInt(user.PERMANENT, 10) === 1 ? (
								<img className="icon2 permanent active lesson-page__user-permanent" alt="" />
							) : (
								''
							)}
						</span>
						{user.ADDPERSON > 0 ? (
							<span className="lesson-page__user-extra">(+{user.ADDPERSON})</span>
						) : (
							''
						)}
					</div>

					<div className="lesson-page__user-actions">
						{selInstructorId === usrInfo.usrId && currentList === 1 && user.STATUS === 0 && user.USRID > 0 ? (
							<button
								type="button"
								className="lesson-page__icon-button"
								onClick={() => handleConfirmConfirmPopup(item.ID, user.USRID, user.USRNAME)}
								aria-label={`Potvrdit platbu klienta ${user.USRNAME}`}
							>
								<img className="icon1 ok0 small2" alt="" />
							</button>
						) : (
							''
						)}

						{selInstructorId === usrInfo.usrId && currentList === 1 && user.STATUS === 0 ? (
							<button
								type="button"
								className="lesson-page__icon-button lesson-page__icon-button--danger"
								onClick={() => handleConfirmPopup(item.ID, user.ID, user.USRNAME)}
								aria-label={`Zrušit účast klienta ${user.USRNAME}`}
							>
								<img className="icon1 del small2" alt="" />
							</button>
						) : (
							''
						)}

						{user.STATUS === 1 ? (
							<span className="lesson-page__user-status" aria-label="Platba potvrzena">
								<img className="icon1 ok1 small2" alt="" />
							</span>
						) : (
							''
						)}
					</div>
				</div>
			));
		}

		return (
			<article className={`lesson-page__card${isLessonLocked ? ' is-locked' : ''}${item.USERS_CNT < usrMax ? ' is-available' : ' is-full'}`} key={`lesson_${item.ID || index}`}>
				<div className="lesson-page__card-head">
					<div className="lesson-page__card-main">
						<div className="lesson-page__card-title">{item.CRDTS_DAY_NAME} - {item.CRDTS_DT}</div>
						<div className="lesson-page__card-subtitle">{item.CRDTS_TM_FROM} - {item.CRDTS_TM_TO} · {item.LNG_H} h</div>
					</div>

					<div className="lesson-page__card-meta">
						<div className="lesson-page__card-meta-top">
							<span className={`lesson-page__capacity${item.USERS_CNT < usrMax ? ' is-available' : ' is-full'}`}>
								{item.USERS_CNT} / {usrMax}
							</span>
							<button
								type="button"
								className={`lesson-page__lock-button${canToggleLessonLock ? ' is-clickable' : ''}`}
								onClick={canToggleLessonLock ? () => saveLessonLocked(item.ID) : undefined}
								disabled={!canToggleLessonLock}
								aria-label={lessonLockAlt}
							>
								<img src={lessonLockIcon} className="lesson-page__lock-icon" alt={lessonLockAlt} />
							</button>
						</div>
						<span className="lesson-page__lesson-id">ID {item.LESSONIDLABEL}</span>
					</div>
				</div>

				{currentList === 1 ? (
					<div className="lesson-page__actions">
						{isOwnInstructor && !isLessonClosed && item.USERS_CNT < usrMax ? (
							<button className="button small green search-new-button lesson-page__action-button" onClick={() => { selLesson(item.ID, 0, 1); }}>
								+1 anonym
							</button>
						) : (
							''
						)}

						{!isOwnInstructor ? (
							<>
								{!isLessonClosed && item.USERS_CNT < usrMax && item.IM_IN === 0 ? (
									<div className="lesson-page__action-group">
										<button className="button small green search-new-button lesson-page__action-button" onClick={() => { selLesson(item.ID, 0, 0); }}>
											Zúčastnit
										</button>
										<button className="button small green search-new-button lesson-page__action-button" onClick={() => { selLesson(item.ID, 1, 0); }}>
											+1
										</button>
										<button className="button small green search-new-button lesson-page__action-button" onClick={() => { selLesson(item.ID, 2, 0); }}>
											+2
										</button>
										<button className="button small green search-new-button lesson-page__action-button" onClick={() => { selLesson(item.ID, 3, 0); }}>
											+3
										</button>

										{showLessonWarning ? (
											<span className="lesson-page__warning">Zbývá míst: {lessonWarning.remainingPlaces}</span>
										) : (
											''
										)}
									</div>
								) : (
									''
								)}

								{item.IM_IN === 1 && item.IM_STATUS === 0 ? (
									<div className="lesson-page__action-group">
										<button className="button small red search-new-button lesson-page__action-button" onClick={() => { unselLesson(item.ID); }}>
											Zrušit účast
										</button>
										<button className="button small search-new-button lesson-page__action-button lesson-page__action-button--payment" onClick={() => showQRPop(item.INSTRUCTOR_QR, item.INSTRUCTOR_PRICE)}>
											QR platba
										</button>
									</div>
								) : (
									''
								)}
							</>
						) : (
							''
						)}

						<button
							type="button"
							className={`lesson-page__lock-button lesson-page__open-button${canToggleLessonLock ? ' is-clickable' : ''}`}
							onClick={canToggleLessonLock ? () => saveLessonOpened(item.ID) : undefined}
							disabled={!canToggleLessonLock}
							aria-label={lessonOpenedAlt}
							title={lessonOpenedAlt}
						>
							<img className={`icon2 lock lesson-page__open-icon${isLessonClosed ? ' active' : ''}`} alt={lessonOpenedAlt} />
						</button>
					</div>
				) : (
					''
				)}

				{item.LESSONNOTE !== '' ? (
					<div className="lesson-page__note" dangerouslySetInnerHTML={{__html: item.LESSONNOTE}}></div>
				) : (
					''
				)}

				{item.USERS_CNT === 0 ? (
					<div className="lesson-page__participants-empty lesson-page__participants-empty--plain">Zatím se nikdo nepřihlásil.</div>
				) : (
					<div className="lesson-page__user-list lesson-page__user-list--plain">{usersList}</div>
				)}
			</article>
		);
	});

	return (
		<>
			<div className="page_main">
				<AppHeader usrInfo={usrInfo} timerCnt={timerCnt} loading={loading} />
				<ConfirmPopup showConfirmPopup={showConfirmPopup} showButtonDelete deleteConfirmPopup={deleteConfirmPopup} closeConfirmPopup={closeConfirmPopup} infoHeader="Zrušení účasti" infoMain={`Opravdu zrušit účast klienta <b>${nameForDelete}</b>?`} />
				<ConfirmPopup showConfirmPopup={showConfirmConfirmPopup} showButtonConfirm cofirmConfirmPopup={confirmLesson} closeConfirmPopup={closeConfirmConfirmPopup} infoHeader="Potvrzení platby" infoMain={`Opravdu potvrdit platbu klienta <b>${nameForDelete}</b>?`} />

				<ConfirmPopup showConfirmPopup={showQR} closeConfirmPopup={closeQR} infoHeader="Platba na účet" infoMain="">
					<div className="lesson-page__qr-popup">
						<div className="lesson-page__qr-code cr_p" onClick={() => clipboard(qrAccount)} dangerouslySetInnerHTML={{__html: qrData}}></div>
						<div className="lesson-page__qr-line cr_p">ČÚ: {qrAccount}</div>
						<input type="button" className="button lesson-page__qr-button" value="Zkopírovat číslo účtu" onClick={() => clipboard(qrAccount)} />
						<div className="lesson-page__qr-line">Cena lekce: {qrPrice}</div>
					</div>
				</ConfirmPopup>

				<div className={`page lesson-page${usrInfo.sysAdmin === 1 || usrInfo.manager === 1 || usrInfo.instructor === 1 ? ' more' : ''}`}>
					<div className="lesson-page__top-panel">
						<section className="lesson-page__section lesson-page__section--coach-filter">
							<div className="lesson-page__section-head lesson-page__section-head--compact">
								<div className="lesson-page__section-copy">
									<h2 className="lesson-page__section-title">Trenéři</h2>
									<div className="lesson-page__section-caption">Filtr lekcí podle vybraného trenéra.</div>
								</div>

								<div className="lesson-page__coach-list lesson-page__coach-list--header">
									{instructors.length > 0 ? (
										instructors.map((instructor) => (
											<button
												key={instructor.CRBY}
												className={`button small search-new-button lesson-page__coach-button${selInstructorId === instructor.CRBY ? ' active is-selected' : ''}`}
												onClick={() => {
													selInstructor(instructor.CRBY);
												}}
											>
												{instructor.USRNAME}
											</button>
										))
									) : (
										<div className="lesson-page__empty lesson-page__empty--inline">Zatím nejsou dostupní žádní trenéři.</div>
									)}
								</div>
							</div>
						</section>

						{selInstructorId > 0 ? (
							<section className="lesson-page__section lesson-page__section--coach-profile lesson-page__profile">
								<InstructorProfile instructor={selectedInstructor} canEditPhoto={usrInfo.usrId === selectedInstructor?.id} />
							</section>
						) : (
							''
						)}
					</div>

					<div className="lesson-page__content lesson-page__content--hidden-scrollbar">
						{selInstructorNote !== '' ? (
							<section className="lesson-page__section">
								<div className="lesson-page__section-head">
									<div>
										<h2 className="lesson-page__section-title">Poznámka trenéra</h2>
									</div>
								</div>
								<div className={`lesson-page__coach-note-wrap${isCoachNoteExpanded ? ' is-expanded' : ''}${canExpandCoachNote ? ' is-collapsible' : ''}`}>
									<div
										ref={coachNoteContentRef}
										className="lesson-page__coach-note"
										style={canExpandCoachNote ? {maxHeight: `${coachNoteMaxHeight}px`} : undefined}
										dangerouslySetInnerHTML={{__html: selInstructorNote}}
									></div>
									{canExpandCoachNote ? (
										<button
											type="button"
											className={`lesson-page__coach-note-toggle${isCoachNoteExpanded ? ' is-expanded' : ''}`}
											onClick={() => setIsCoachNoteExpanded((prevState) => !prevState)}
											aria-expanded={isCoachNoteExpanded}
											aria-label={isCoachNoteExpanded ? 'Sbalit poznámku trenéra' : 'Rozbalit poznámku trenéra'}
										>
											<span className="lesson-page__coach-note-toggle-icon" aria-hidden="true"></span>
										</button>
										) : (
											''
									)}
								</div>
							</section>
						) : (
							''
						)}

						<section className="lesson-page__section lesson-page__section--list">
							<div className="lesson-page__section-head lesson-page__section-head--list">
								<div>
									<h2 className="lesson-page__section-title">{viewTitle}</h2>
								</div>

								<div className="lesson-page__view-switch">
									{currentList === 1 ? (
										<button type="button" className="lesson-page__view-button" onClick={() => prevLessons()} aria-label="Zobrazit minulé lekce">
											<img className="icon_left" alt="" />
										</button>
									) : (
										<button type="button" className="lesson-page__view-button" onClick={() => currLessons()} aria-label="Zobrazit nadcházející lekce">
											<img className="icon_right" alt="" />
										</button>
									)}
								</div>
							</div>

							<div className="lesson-page__lesson-list">
								{data?.length === 0 ? (
									<div className="lesson-page__empty">Žádné lekce.</div>
								) : (
									list
								)}
							</div>

							<div className="lesson-page__load-more">
								{parseInt(loadedCnt, 10) === listRows && listRows > 0 ? (
									<button className="button small loadNext lesson-page__button" onClick={() => loadNext()}>
										Načíst další
									</button>
								) : (
									<span className="fnt_12 loadNext lesson-page__load-all">Byly načteny všechny záznamy</span>
								)}
							</div>
						</section>
					</div>
				</div>
			</div>
		</>
	);
}
